import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BottomNav from '../components/BottomNav';
import UserHeader from '../components/UserHeader';
import {
  getParasha,
  getTradition,
  setSelectedSection,
  getLearningTargets,
  setLearningTargets,
  LearningTargetKey,
} from '../utils/userSelections';
import { getAliyahMetas, getParashaVerses } from '../utils/aliyot';
import { getHaftaraTitle } from '../utils/haftarot';
import { loadBestScores } from '../utils/progressStorage';
import { useTraditionRabbi } from '../utils/rabbiesFirestore';
import { trackClick, trackNavigation, trackPageView } from '../utils/analytics';
import starFilledIcon from '../assets/icon-star-filled.svg';
import starOutlineIcon from '../assets/icon-star-outline.svg';

const CLOUDFRONT_BASE = 'https://d1q1chclchjxl4.cloudfront.net';
const PARASHA_IMAGE_BASE = `${CLOUDFRONT_BASE}/images`;

const BLUE_ACCENT = '#3B7BF4';
const GOLD = '#F5C842';
const TEXT_DARK = '#1A1A2E';
const TEXT_SECONDARY = '#6B7280';
const BORDER_LIGHT = '#E5E7EB';

interface AliyahInfo {
  label: string;
  verseStart: number;
  verseEnd: number;
  globalIndices: number[];
  progress: number;
  isMaftir: boolean;
}

// ─── Shared card row ───────────────────────────────────────────────────────
// Direction is always LTR so: [% left] [text center/RTL] [action right]
interface CardRowProps {
  progress: number;
  label: string;
  subtitle?: string;
  onPress: () => void;
  highlighted?: boolean;
  isContinue?: boolean;
  /** undefined = user never chose learning targets, so no star is shown */
  starred?: boolean;
  /** when provided, clicking the star toggles selection instead of navigating */
  onToggleStar?: () => void;
}

const CardRow: React.FC<CardRowProps> = ({
  progress,
  label,
  subtitle,
  onPress,
  highlighted = false,
  isContinue = false,
  starred = undefined,
  onToggleStar,
}) => {
  const { t } = useTranslation();
  const hasProgress = progress > 0;
  const active = highlighted || hasProgress || isContinue;
  const showContinueButton = isContinue || highlighted;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onPress}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onPress();
        }
      }}
      style={{
        background: isContinue || highlighted ? '#DDE9F9' : '#ffffff',
        borderRadius: 18,
        padding: '14px 16px',
        border: active ? `1.5px solid ${BLUE_ACCENT}22` : `1px solid ${BORDER_LIGHT}`,
        boxShadow: active ? '0 4px 18px rgba(59,123,244,0.09)' : '0 1px 4px rgba(0,0,0,0.05)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        direction: 'ltr', // keeps % on left, button on right regardless of locale
        userSelect: 'none',
      }}
    >
      {/* Star — shown only when the user picked learning targets during onboarding */}
      {starred !== undefined && (
        <img
          src={starred ? starFilledIcon : starOutlineIcon}
          alt=""
          aria-hidden="true"
          onClick={
            onToggleStar
              ? (e) => {
                  e.stopPropagation();
                  onToggleStar();
                }
              : undefined
          }
          style={{
            width: 22,
            height: 21,
            flexShrink: 0,
            cursor: onToggleStar ? 'pointer' : undefined,
          }}
        />
      )}

      {/* Progress % — always on the left */}
      <div
        style={{
          minWidth: 42,
          fontWeight: 800,
          fontSize: hasProgress ? 17 : 14,
          color: hasProgress ? BLUE_ACCENT : '#9CA3AF',
          textAlign: 'center',
          flexShrink: 0,
          letterSpacing: -0.5,
        }}
      >
        {progress}%
      </div>

      {/* Label + subtitle — RTL text, centred in remaining space */}
      <div style={{ flex: 1, minWidth: 0, direction: 'rtl', textAlign: 'right' }}>
        <div
          style={{
            fontSize: 15,
            fontWeight: hasProgress ? 800 : 600,
            color: TEXT_DARK,
            lineHeight: 1.25,
          }}
        >
          {label}
        </div>
        {subtitle && (
          <div
            style={{
              fontSize: 11,
              color: TEXT_SECONDARY,
              marginTop: 2,
              fontWeight: 400,
            }}
          >
            {subtitle}
          </div>
        )}
      </div>

      {/* Action — gold "Continue" for the last-practiced aliyah and for chosen learning targets */}
      {showContinueButton ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPress();
          }}
          style={{
            background: GOLD,
            border: 'none',
            borderRadius: 10,
            padding: '8px 16px',
            fontWeight: 700,
            fontSize: 13,
            color: TEXT_DARK,
            cursor: 'pointer',
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {t('parasha_overview_continue', 'המשך')}
        </button>
      ) : (
        <span
          style={{
            fontSize: 20,
            color: '#CBD5E1',
            flexShrink: 0,
            lineHeight: 1,
          }}
        >
          ›
        </span>
      )}
    </div>
  );
};

// ─── Rabbi chip ────────────────────────────────────────────────────────────
interface RabbiChipProps {
  rabbiName: string;
  nusachLabel: string;
  imageUrl?: string;
}

const WaveformIcon: React.FC = () => (
  <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0" y="4" width="2.5" height="6" rx="1.25" fill="#F5C842" />
    <rect x="3.5" y="1" width="2.5" height="12" rx="1.25" fill="#F5C842" />
    <rect x="7" y="3" width="2.5" height="8" rx="1.25" fill="#F5C842" />
    <rect x="10.5" y="0" width="2.5" height="14" rx="1.25" fill="#F5C842" />
    <rect x="14" y="3" width="2.5" height="8" rx="1.25" fill="#F5C842" />
    <rect x="17.5" y="5" width="2.5" height="4" rx="1.25" fill="#F5C842" />
  </svg>
);

const RabbiChip: React.FC<RabbiChipProps> = ({ rabbiName, nusachLabel, imageUrl }) => {
  const { t } = useTranslation();
  return (
    /* Outer row: right-aligned, RTL so photo is on far right */
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        marginTop: 10,
        marginBottom: 10,
        justifyContent: 'center',
        direction: 'ltr', // control order manually
      }}
    >
      {/* Pill — sits to the left of the photo */}
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          background: '#EFEFEF',
          borderRadius: '20px 0 0 20px',
          padding: '7px 14px 7px 12px',
          fontSize: 13,
          color: TEXT_SECONDARY,
          fontWeight: 500,
          direction: 'rtl',
          whiteSpace: 'nowrap',
        }}
      >
        <WaveformIcon />
        <span>
          <span style={{ color: TEXT_SECONDARY, fontWeight: 400 }}>
            {t('parasha_overview_mikra', 'מקריא')}:{' '}
          </span>
          <span style={{ color: TEXT_DARK, fontWeight: 700 }}>{rabbiName}</span>
          {'  '}
          <span style={{ color: TEXT_SECONDARY, fontWeight: 400 }}>
            {t('parasha_overview_nusach', 'נוסח')}:{' '}
          </span>
          <span style={{ color: TEXT_DARK, fontWeight: 700 }}>{nusachLabel}</span>
        </span>
      </div>

      {/* Rabbi photo — circle on the far right, overlaps pill slightly */}
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          overflow: 'hidden',
          border: '2px solid white',
          flexShrink: 0,
          background: '#D1D5DB',
          marginLeft: -4,
        }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={rabbiName}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              background: '#E5E7EB',
            }}
          >
            👤
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Section header ────────────────────────────────────────────────────────
interface SectionHeaderProps {
  label: string;
}
const SectionHeader: React.FC<SectionHeaderProps> = ({ label }) => (
  <div
    style={{
      fontSize: 13,
      fontWeight: 700,
      color: TEXT_SECONDARY,
      direction: 'rtl',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginBottom: 8,
      marginTop: 20,
      paddingInlineStart: 2,
    }}
  >
    {label}
  </div>
);

// ─── Main page ─────────────────────────────────────────────────────────────
const ParashaOverviewPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isHebrew = i18n.language?.startsWith('he');

  const selectedParasha = getParasha();
  const parashaKey = selectedParasha?.english;
  const tradition = getTradition();
  const nusach = tradition?.key || 'ashkenaz';
  const parashaRabbi = useTraditionRabbi('parasha');
  const haftaraRabbi = useTraditionRabbi('haftara');

  const [bestScores, setBestScores] = useState<Record<string, number>>({});
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    trackPageView('ParashaOverview', { parasha: parashaKey });
    setBestScores(loadBestScores());
  }, [parashaKey]);

  const parashaVerses = useMemo(
    () => (parashaKey ? getParashaVerses(parashaKey) : []),
    [parashaKey],
  );

  const aliyahInfos = useMemo<AliyahInfo[]>(() => {
    if (!parashaKey) return [];
    return getAliyahMetas(parashaKey, isHebrew).map((meta) => {
      const practiced = meta.globalIndices.filter(
        (idx) => bestScores[`${parashaKey}:${idx}`] !== undefined,
      ).length;
      const progress =
        meta.globalIndices.length > 0
          ? Math.round((practiced / meta.globalIndices.length) * 100)
          : 0;
      return { ...meta, progress };
    });
  }, [parashaKey, bestScores, isHebrew]);

  // Readings the user chose to learn during onboarding (null = never chose, no stars shown)
  const [learningTargets, setLearningTargetsState] = useState<Set<LearningTargetKey> | null>(() => {
    const targets = getLearningTargets();
    return targets === null ? null : new Set(targets);
  });
  const isTarget = (key: LearningTargetKey): boolean => learningTargets?.has(key) ?? false;
  const starredFor = (key: LearningTargetKey): boolean | undefined =>
    learningTargets === null ? undefined : learningTargets.has(key);

  const toggleAliyahStar = (idx: number): void => {
    if (learningTargets === null) return;
    const key: LearningTargetKey = `aliyah_${idx}`;
    const next = new Set(learningTargets);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setLearningTargetsState(next);
    setLearningTargets(Array.from(next));
    trackClick('Aliyah Star', 'ParashaOverview', { aliyahIdx: idx, starred: next.has(key) });
  };

  // Which global verse index was last practiced?
  const lastGlobalVerse = useMemo<number | null>(() => {
    if (!parashaKey) return null;
    try {
      const saved = localStorage.getItem(`lastComparedVerse:${parashaKey}`);
      if (saved === null) return null;
      const v = parseInt(saved, 10);
      return isNaN(v) ? null : v;
    } catch {
      return null;
    }
  }, [parashaKey]);

  // Which aliyah contains the last practiced verse?
  const continueAliyahIdx = useMemo<number | null>(() => {
    if (lastGlobalVerse === null) return null;
    for (let i = 0; i < aliyahInfos.length; i++) {
      if (aliyahInfos[i].globalIndices.includes(lastGlobalVerse)) return i;
    }
    return null;
  }, [aliyahInfos, lastGlobalVerse]);

  // Local index of that verse within its aliyah (for PracticePage to start at the right spot)
  const continueVerseLocalIdx = useMemo<number>(() => {
    if (continueAliyahIdx === null || lastGlobalVerse === null) return 0;
    const idx = aliyahInfos[continueAliyahIdx].globalIndices.indexOf(lastGlobalVerse);
    return idx >= 0 ? idx : 0;
  }, [aliyahInfos, continueAliyahIdx, lastGlobalVerse]);

  const haftaraTitle = useMemo<string | null>(
    () => (parashaKey ? getHaftaraTitle(parashaKey, tradition?.key ?? null) : null),
    [parashaKey, tradition?.key],
  );

  // ── Derived display values ────────────────────────────────────────────────
  const parashaImageUrl = parashaKey
    ? `${PARASHA_IMAGE_BASE}/${parashaKey.toLowerCase()}.webp`
    : null;

  const getRabbiDisplayName = (rabbi: ReturnType<typeof useTraditionRabbi>) => {
    if (!rabbi) return undefined;
    return isHebrew
      ? rabbi.nameHe || rabbi.nameEn || rabbi.initials
      : rabbi.nameEn || rabbi.nameHe || rabbi.initials;
  };

  const parashaRabbiName = getRabbiDisplayName(parashaRabbi);
  const haftaraRabbiName = getRabbiDisplayName(haftaraRabbi);
  const nusachLabel = (() => {
    const key = nusach === 'sephardi' ? 'sfarad' : nusach === 'yemenite' ? 'yamen' : nusach;
    const translated = t(`tradition.${key}`);
    return translated !== `tradition.${key}`
      ? translated
      : nusach.charAt(0).toUpperCase() + nusach.slice(1);
  })();

  const handleAliyahPress = (aliyahIdx: number) => {
    trackClick('Aliyah Card', 'ParashaOverview', { aliyahIdx });
    trackNavigation('ParashaOverview', '/practice', 'aliyah_card');
    setSelectedSection({ type: 'aliyah', aliyahIndex: aliyahIdx });
    const isContinueAliyah = aliyahIdx === continueAliyahIdx;
    navigate('/practice', {
      state: {
        aliyahIndex: aliyahIdx,
        ...(isContinueAliyah ? { startVerseIndex: continueVerseLocalIdx } : {}),
      },
    });
  };

  const handleHaftaraPress = () => {
    trackClick('Haftara Card', 'ParashaOverview');
    trackNavigation('ParashaOverview', '/practice', 'haftara_card');
    setSelectedSection({ type: 'haftara' });
    navigate('/practice', {
      state: {
        mode: 'haftara',
      },
    });
  };

  const parashaHebrewName = selectedParasha?.hebrew || '';

  // ── Empty state ───────────────────────────────────────────────────────────
  if (!selectedParasha) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          gap: 16,
          background: '#f9fafb',
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 700, color: TEXT_DARK }}>
          {t('parasha_overview_no_parasha', 'אנא בחר פרשה')}
        </div>
        <button
          onClick={() => navigate('/choose-parasha')}
          style={{
            background: BLUE_ACCENT,
            color: 'white',
            border: 'none',
            borderRadius: 12,
            padding: '12px 28px',
            fontWeight: 700,
            fontSize: 15,
            cursor: 'pointer',
          }}
        >
          {t('choose_parasha', 'בחר פרשה')}
        </button>
        <BottomNav />
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        background: '#F3F4F8',
        fontFamily: "var(--kol-font-sans, 'Inter', system-ui, sans-serif)",
        color: TEXT_DARK,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <UserHeader />
      <div
        style={{
          padding: '16px 16px 110px',
          boxSizing: 'border-box',
          direction: 'rtl',
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {/* ── Hero image ───────────────────────────────────────────────────── */}
        <div
          style={{
            position: 'relative',
            borderRadius: 22,
            overflow: 'hidden',
            marginBottom: 20,
            background: 'linear-gradient(160deg, #1a3a6b 0%, #3B7BF4 100%)',
            minHeight: imageLoaded && !imageError ? 0 : 160,
          }}
        >
          {/* Image — hidden until loaded */}
          {parashaImageUrl && !imageError && (
            <img
              src={parashaImageUrl}
              alt={parashaHebrewName}
              style={{
                width: '100%',
                height: 210,
                objectFit: 'cover',
                display: 'block',
                opacity: imageLoaded ? 1 : 0,
                transition: 'opacity 0.3s',
              }}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          )}

          {/* Fallback text: only shown when image has NOT loaded or failed */}
          {(!imageLoaded || imageError) && (
            <div
              style={{
                position: imageLoaded ? 'relative' : 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '28px 20px',
                gap: 8,
                minHeight: 160,
              }}
            >
              <div
                style={{
                  fontSize: 44,
                  fontWeight: 900,
                  color: 'white',
                  textShadow: '0 2px 16px rgba(0,0,0,0.4)',
                  lineHeight: 1.1,
                  textAlign: 'center',
                  direction: 'rtl',
                }}
              >
                {parashaHebrewName}
              </div>
              {parashaVerses[0]?.hebrew && (
                <div
                  style={{
                    fontSize: 12,
                    color: 'rgba(255,255,255,0.8)',
                    textAlign: 'center',
                    maxWidth: 260,
                    lineHeight: 1.5,
                    direction: 'rtl',
                  }}
                >
                  {parashaVerses[0].hebrew.split(' ').slice(0, 7).join(' ')}...
                </div>
              )}
            </div>
          )}

          {/* Subtle bottom gradient over the image for depth */}
          {imageLoaded && !imageError && (
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 60,
                background: 'linear-gradient(to top, rgba(0,0,0,0.18) 0%, transparent 100%)',
                pointerEvents: 'none',
              }}
            />
          )}
        </div>

        {/* ── Haftara section ──────────────────────────────────────────────── */}
        {haftaraRabbiName && (
          <RabbiChip
            rabbiName={haftaraRabbiName}
            nusachLabel={nusachLabel}
            imageUrl={haftaraRabbi?.imageUrl}
          />
        )}

        <CardRow
          progress={0}
          label={
            haftaraTitle
              ? (t('haftara_with_title', { title: haftaraTitle }) as string)
              : t('parasha_overview_haftara', 'הפטרה')
          }
          onPress={handleHaftaraPress}
          highlighted={isTarget('haftara')}
          starred={starredFor('haftara')}
        />

        {/* ── Torah aliyot section ─────────────────────────────────────────── */}
        {parashaRabbiName && (
          <RabbiChip
            rabbiName={parashaRabbiName}
            nusachLabel={nusachLabel}
            imageUrl={parashaRabbi?.imageUrl}
          />
        )}

        {!parashaRabbiName && !haftaraRabbiName && (
          <SectionHeader label={isHebrew ? 'עלייות' : 'Aliyot'} />
        )}
        {parashaRabbiName && <div style={{ height: 4 }} />}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {aliyahInfos.map((aliyah, idx) => (
            <CardRow
              key={idx}
              progress={aliyah.progress}
              label={aliyah.label}
              subtitle={
                t('parasha_overview_verses', {
                  start: aliyah.verseStart,
                  end: aliyah.verseEnd,
                }) as string
              }
              onPress={() => handleAliyahPress(idx)}
              isContinue={idx === continueAliyahIdx}
              highlighted={isTarget(`aliyah_${idx}`)}
              starred={starredFor(`aliyah_${idx}`)}
              onToggleStar={() => toggleAliyahStar(idx)}
            />
          ))}
        </div>

        {/* ── Special readings ─────────────────────────────────────────────── */}
        <SectionHeader label={isHebrew ? 'קריאות נוספות' : 'Additional Readings'} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <CardRow
            progress={0}
            label={t('parasha_overview_birchot_hatorah', 'ברכות התורה')}
            subtitle={
              t(
                'parasha_overview_birchot_hatorah_subtitle',
                'נקראות לפני ואחרי קריאה בתורה',
              ) as string
            }
            onPress={() => {
              trackClick('Birchot Hatorah Card', 'ParashaOverview');
              navigate('/birchot-hatorah');
            }}
            highlighted={isTarget('birchot_hatorah')}
            starred={starredFor('birchot_hatorah')}
          />
          <CardRow
            progress={0}
            label={t('parasha_overview_shema', 'שמע ישראל')}
            subtitle={t('parasha_overview_shema_subtitle', 'פסוקים א׳–ו׳') as string}
            onPress={() => {
              trackClick('Shema Card', 'ParashaOverview');
              navigate('/shema');
            }}
            highlighted={isTarget('shema')}
            starred={starredFor('shema')}
          />
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default ParashaOverviewPage;
